<template>
  <div class="eyebrow">
    {{ giftCardTitle }}
  </div>
  <div class="h4 margin-top-xs">Gift Card</div>
  <div class="gift-card-image" :class="entryPoint">
    <AtomicUiPicture :image-url="giftCardImageUrl" :using-scene7="false" />
  </div>
  <div class="margin-top-l">
    <p class="body">CHOOSE AN AMOUNT:</p>
    <div class="margin-top-xs">
      <AtomicUiHorizontalFacets name="facet" :options="amountOptionsArr" @on-facet-click="selectedAmountOption" />
    </div>
  </div>
  <div class="margin-top-s input-text-field">
    <div class="input-container">
      <AtomicUiInputText
        v-model="giftCardAmount"
        name="giftCardAmount"
        :hide-error-message="true"
        label="Enter a custom amount*"
        mask="currencyFormat"
        :class="{ 'is-invalid-input': amountInputError }"
        data-fs-mask
        @keyup="giftAmountChangeEvent()"
      />
    </div>
    <div v-if="amountInputError" class="error-color grid-x align-middle">
      <span class="error-red small-1 margin-right-xxs"></span>
      <span>Enter an amount from $10 to $1,000</span>
    </div>
  </div>
  <div v-if="giftCardEnabled && eGiftCardEnabled && eGiftEnabled && physicalGiftCardEnabled" class="margin-top-s">
    <p class="body">DELIVERY:</p>
    <div class="margin-top-xs">
      <AtomicUiHorizontalFacets name="facet" :options="deliveryOptionsArr" class="delivery-option" @on-facet-click="selectedMailOption" />
    </div>
  </div>
  <div class="margin-top-m">
    <div class="grid-x message-block">
      <div class="body small-6 grid-x">YOUR MESSAGE:</div>
      <div class="small grey-text small-6 grid-x align-right">(optional)</div>
    </div>

    <div class="margin-top-s">
      <textarea
        v-model="giftCardMessage"
        aria-label="Message"
        placeholder="Message:* "
        type="text"
        class="padding-s textarea-address medium"
        rule="giftMessage"
        maxlength="60"
        data-fs-mask
        @change="onChangeGiftMessage"
      ></textarea>
      <p class="body grey-text">Your gift message can be up to 60 characters</p>
    </div>
  </div>
</template>
<script setup>
import { giftGiverProductCard } from 'config';
import { useGiftCard } from '~/composables/useGiftCard';
import { useEnvPropsStore } from '~/stores/envProps';
import { useQuickViewStore } from '~/stores/quickView';
import { SET_IN_VALID_GIFT_CARD_AMOUNT, SET_GIFT_CARD_AMOUNT } from '~/types/mutations';

const props = defineProps({
  product: {
    type: Object,
    required: false,
  },
  eGiftEnabled: {
    type: Boolean,
    required: true,
  },
  physicalGiftCardEnabled: {
    type: Boolean,
    required: true,
  },
});

const envPropsStore = useEnvPropsStore();
const quickViewStore = useQuickViewStore();
const { giftCardTitle, giftCardEnabled, eGiftCardEnabled } = useGiftCard({
  propProduct: props.product,
  eGiftEnabled: props.eGiftEnabled,
});
const cdnUrl = envPropsStore.envProps?.cdnUrl || '';

const giftCardImageUrl = `${cdnUrl}/img/${giftGiverProductCard.giftCardImageUrl}`;

const giftCardMessage = ref('');
const giftCardAmount = ref('');
const selectedAmountOptionId = ref(null);
const selectedDeliveryOption = ref('E-mail');
const amountInputError = ref(false);
const amountOptionsArr = computed(() => [
  {
    id: 1,
    contents: { text: '$10' },
    outlined: selectedAmountOptionId.value === 1,
  },
  {
    id: 2,
    contents: { text: '$50' },
    outlined: selectedAmountOptionId.value === 2,
  },
  {
    id: 3,
    contents: { text: '$100' },
    outlined: selectedAmountOptionId.value === 3,
  },
  {
    id: 4,
    contents: { text: '$500' },
    outlined: selectedAmountOptionId.value === 4,
  },
]);
const deliveryOptionsArr = computed(() => [
  {
    id: 1,
    contents: { text: 'E-mail' },
    outlined: selectedDeliveryOption.value === 'E-mail',
  },
  {
    id: 2,
    contents: { text: 'Mail' },
    outlined: selectedDeliveryOption.value === 'Mail',
  },
]);
const entryPoint = computed(() => envPropsStore.envProps.entryPoint?.toLowerCase());
// methods
const giftAmountChangeEvent = (external = false) => {
  const amount = giftCardAmount.value ? Number(giftCardAmount.value.slice(1)) : 0;
  if (amount >= 10 && amount <= 1000) {
    amountInputError.value = false;
    quickViewStore[SET_IN_VALID_GIFT_CARD_AMOUNT](false);
    quickViewStore[SET_GIFT_CARD_AMOUNT](giftCardAmount.value);
  } else {
    amountInputError.value = true;
    quickViewStore[SET_IN_VALID_GIFT_CARD_AMOUNT](true);
    quickViewStore[SET_GIFT_CARD_AMOUNT](0);
  }
  if (!external) {
    selectedAmountOptionId.value = 0;
  }
};
const selectedAmountOption = (value) => {
  const clickedId = value?.selected?.id;
  if (selectedAmountOptionId.value && selectedAmountOptionId.value === clickedId) {
    // Deselection
    selectedAmountOptionId.value = null;
    giftCardAmount.value = '';
    amountInputError.value = false;
    quickViewStore[SET_IN_VALID_GIFT_CARD_AMOUNT](false);
    quickViewStore[SET_GIFT_CARD_AMOUNT](0);
  } else {
    // Selection
    selectedAmountOptionId.value = clickedId;
    giftCardAmount.value = value?.selected?.contents?.text;
    giftAmountChangeEvent(true);
  }
};
const onChangeGiftMessage = () => {
  quickViewStore.setGiftCardMessage(giftCardMessage.value);
};
const updateDeliveryOption = () => {
  quickViewStore.setGiftCardDeliveryOption(selectedDeliveryOption.value);
};
const selectedMailOption = (value) => {
  selectedDeliveryOption.value = value?.selected?.contents?.text;
  updateDeliveryOption();
};
onMounted(() => {
  updateDeliveryOption();
});
</script>

<style lang="scss" scoped>
@import '@csgIcons/error-red.scss';

.gift-card-image.mcom {
  width: rem-calc(422);
  height: rem-calc(265);
  margin-left: rem-calc(40);
  margin-top: rem-calc(36);
}

.gift-card-image.bcom {
  margin-top: rem-calc(36);
}

textarea {
  width: rem-calc(486);
  height: rem-calc(103);
  line-height: rem-calc(20);
}

.grey-text {
  color: $gray-1-color;
}

.delivery-option {
  :deep(.csg-horizontal-facet.selected) {
    pointer-events: none;
  }
}

@include breakpoint(small down) {
  .gift-card-image.mcom {
    width: rem-calc(325);
    height: rem-calc(210);
    margin-left: rem-calc(10);
    margin-top: rem-calc(31);
  }

  .gift-card-image.bcom {
    margin-top: rem-calc(31);
  }

  .input-text-field {
    width: rem-calc(331);
    height: rem-calc(50);
  }

  textarea {
    width: rem-calc(335);
    height: rem-calc(103);
    line-height: rem-calc(20);
  }

  .message-block {
    width: rem-calc(335);
  }
}
</style>
